import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Mail, Calendar, Send, Check, Users } from "lucide-react";

export default function RsvpPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    city: "",
    state: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [stats, setStats] = useState({ total: 0, top_cities: [] });
  const [inventory, setInventory] = useState([]);

  useEffect(() => {
    api.get("/rsvp/stats").then((r) => setStats(r.data || { total: 0, top_cities: [] }));
    api.get("/sponsorship/inventory").then((r) => setInventory(r.data || []));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/rsvp/signup", { ...form, source: "rsvp_page" });
      setDone({
        already: data.already,
        city: data.signup?.city || form.city,
      });
      // refresh stats
      api.get("/rsvp/stats").then((r) => setStats(r.data || { total: 0, top_cities: [] }));
      toast(data.already ? "Already on the list." : "You're on the list.");
    } catch (e2) {
      toast(formatApiError(e2?.response?.data?.detail) || "Could not sign up.");
    } finally {
      setSubmitting(false);
    }
  };

  const pickCity = (city, state) => setForm((f) => ({ ...f, city, state: state || f.state }));

  return (
    <div data-testid="rsvp-page">
      <section className="relative grain splatter overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-8 pt-16 pb-16 grid md:grid-cols-12 gap-10 items-start relative z-10">
          <div className="md:col-span-7">
            <div className="overline">RSVP · Tour List</div>
            <h1 className="font-display text-6xl md:text-8xl uppercase mt-3 leading-[0.85] drip-shadow">
              When I roll into
              <br />
              <span className="text-[#FF1493]">your city,</span>
              <br />
              you'll know first.
            </h1>
            <p className="text-white/75 mt-7 text-lg max-w-2xl">
              Drop your name, your city, and we'll ping you the moment FMAA hits your zip code —
              meetups, drops, content unlocks. Free. No spam. Just signals.
            </p>
            <div className="mt-6 flex items-center gap-2 text-sm text-white/60">
              <Users size={14} className="text-[#FF1493]" />
              <span data-testid="rsvp-total">
                {stats.total.toLocaleString()} fans already on the list
              </span>
            </div>
          </div>

          <div className="md:col-span-5">
            {done ? (
              <div
                className="border border-[#FFC107]/50 bg-[#141414] p-8 text-center"
                data-testid="rsvp-success"
              >
                <div className="w-14 h-14 mx-auto bg-[#FFC107] text-[#0A0A0A] flex items-center justify-center">
                  <Check size={26} />
                </div>
                <div className="overline mt-5">
                  {done.already ? "Already there" : "You're in"}
                </div>
                <h3 className="font-display text-3xl uppercase mt-3">
                  See you in {done.city}.
                </h3>
                <p className="text-white/70 text-sm mt-3">
                  We'll ping you the moment the Sentra crosses your city line.
                </p>
                <button
                  onClick={() => {
                    setDone(null);
                    setForm({ name: "", email: "", city: "", state: "", message: "" });
                  }}
                  className="btn-ghost mt-6"
                  data-testid="rsvp-another"
                >
                  Add another city
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="tac-card p-7 space-y-3"
                data-testid="rsvp-form"
              >
                <div className="overline">Sign up · 20 seconds</div>
                <input
                  required
                  placeholder="Your name"
                  className="tac-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="rsvp-name"
                />
                <input
                  required
                  type="email"
                  placeholder="Email"
                  className="tac-input"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="rsvp-email"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    required
                    placeholder="City"
                    className="tac-input"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    data-testid="rsvp-city"
                  />
                  <input
                    placeholder="State (optional)"
                    className="tac-input"
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value })}
                    data-testid="rsvp-state"
                  />
                </div>
                <textarea
                  rows={3}
                  placeholder="Anything we should know? (optional)"
                  className="tac-input"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="rsvp-message"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-blaze w-full justify-center"
                  data-testid="rsvp-submit"
                >
                  <Send size={14} /> {submitting ? "Adding..." : "Add me to the list"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Upcoming cities — quick pick */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-12">
        <div className="overline">Coming up</div>
        <h2 className="font-display text-3xl md:text-4xl uppercase mt-3">Pick your stop</h2>
        <p className="text-white/60 text-sm mt-2">
          Tap a city to pre-fill the sign-up above. Don't see yours? Type it anyway.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-7">
          {inventory.map((s) => (
            <button
              key={s.id}
              onClick={() => pickCity(s.city, s.state)}
              className="tac-card p-4 text-left hover:border-[#FF1493]/60 transition"
              data-testid={`rsvp-pick-${s.city.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] tracking-[0.2em] uppercase text-white/50 font-mono-tactical">
                  <Calendar size={9} className="inline mr-1" />
                  {new Date(s.scheduled_date).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                  })}
                </span>
                <span
                  className="text-[10px] tracking-[0.2em] uppercase font-display"
                  style={{ color: s.status === "available" ? "#FF1493" : "#FFC107" }}
                >
                  {s.status === "available" ? "Open" : "Sold"}
                </span>
              </div>
              <div className="font-display text-xl uppercase mt-2">{s.city}</div>
              <div className="text-[11px] text-white/50 font-mono-tactical">{s.state}</div>
            </button>
          ))}
        </div>
      </section>

      {/* Top cities */}
      {stats.top_cities.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-8 py-12 pb-24">
          <div className="overline">Hot cities</div>
          <h2 className="font-display text-3xl md:text-4xl uppercase mt-3">Where the fans are</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-7">
            {stats.top_cities.map((c, i) => (
              <div key={c.city} className="tac-card p-5" data-testid={`hot-city-${i}`}>
                <div className="text-[10px] tracking-[0.2em] uppercase font-mono-tactical text-white/50">
                  #{String(i + 1).padStart(2, "0")}
                </div>
                <div className="font-display text-2xl uppercase mt-2 flex items-center gap-2">
                  <MapPin size={14} className="text-[#FF1493]" />
                  {c.city}
                </div>
                <div className="font-display text-3xl text-[#FF1493] mt-1">
                  {c.count.toLocaleString()}
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-mono-tactical">
                  fans waiting
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
